import type { ReactNode } from 'react';
import { MAX_DURATION_MINUTES, MAX_OFFSET_MINUTES } from '@vendorops/shared';
import { Input } from '@/components/ui/input';

/**
 * A span of time, said the way a planner says it.
 *
 * "An hour and a half" is `1` hr `30` min. One minute count would make it 90,
 * which is arithmetic the product should be doing, and it is the same arithmetic
 * whether the span is how long an item runs or how far it sits from the start of
 * the wedding — so both are this control. Two grammars in one form is how a
 * planner types the right number into the wrong field.
 *
 * The pair is held as text, not as a number, for one reason that is load-bearing
 * everywhere it is used: **an empty pair and a typed zero are different
 * answers.** For a duration, empty means open-ended — runs to the next item, or
 * to the end of the night — and zero means a point in time, the send-off at the
 * exact moment the night ends. `minutesFromFields` returns `null` for the first
 * and `0` for the second, and every caller has to decide what each one means
 * rather than being handed a number that has already forgotten the difference.
 */

/** What the two fields hold. Text, because `''` is an answer and `0` is another. */
export interface HoursMinutes {
  hours: string;
  minutes: string;
}

export const EMPTY_HOURS_MINUTES: HoursMinutes = { hours: '', minutes: '' };

/** An empty field is zero; anything that is not a whole count is not a number. */
function countOf(text: string): number {
  const trimmed = text.trim();
  if (trimmed === '') return 0;
  const value = Number(trimmed);
  return Number.isInteger(value) && value >= 0 ? value : NaN;
}

/**
 * The pair as minutes — `null` when both fields are empty, `NaN` when either is
 * not a whole count.
 *
 * Three outcomes because the field has three states, and collapsing the empty
 * pair into `0` is precisely the bug this shape exists to prevent.
 */
export function minutesFromFields(value: HoursMinutes): number | null {
  if (value.hours.trim() === '' && value.minutes.trim() === '') return null;
  return countOf(value.hours) * 60 + countOf(value.minutes);
}

/**
 * Whether a typed pair is past a cap, which is asked of two different spans.
 *
 * The caps are two numbers for two reasons — an item may *run* a day and may
 * *sit* two days from the start — and both come from the schema that will
 * answer for them, because a form with a ceiling of its own is a form that can
 * refuse what the API accepts. One local `1440` served as both while the two
 * happened to agree, and became exactly that bug the moment the schema's offset
 * bound widened to two days and this copy of it did not.
 *
 * `null` is neither out of range nor in it: the pair is empty, not wrong.
 */
function outOfRange(minutes: number | null, cap: number): boolean {
  return minutes !== null && (Number.isNaN(minutes) || minutes > cap);
}

/** Not a whole count, or longer than an item may run. */
export function durationOutOfRange(minutes: number | null): boolean {
  return outOfRange(minutes, MAX_DURATION_MINUTES);
}

/**
 * What a planner is told when a length is past the cap.
 *
 * Beside `durationOutOfRange` so the message and the number it describes are
 * one thing, the way the server pairs its own bound with
 * `DURATION_TOO_LONG_MESSAGE`. Three forms ask this question and all three
 * carried their own copy of it; a cap that moves without its sentence moving is
 * the copy-shaped half of the bug this file already had once.
 */
export const DURATION_TOO_LONG_HINT = 'Hours and minutes, no more than a day';

/**
 * Not a whole count, or further from the start than an item may sit.
 *
 * Signed or not: the before/after direction is a separate control on the form
 * this serves, so the cap is the same in both directions and the sign is the
 * caller's presentation rather than this function's precondition.
 */
export function offsetOutOfRange(offsetMinutes: number | null): boolean {
  return outOfRange(offsetMinutes === null ? null : Math.abs(offsetMinutes), MAX_OFFSET_MINUTES);
}

/**
 * What a planner is told when an offset is past the cap.
 *
 * In hours, not days, because the readback beside it is `formatDuration`, which
 * never reaches a day: a field that answers "48 hr before start" must not be
 * corrected in units it does not speak.
 *
 * Exported beside `offsetOutOfRange` for the same reason the duration hint is,
 * even though only one form asks it today. The alternative puts the `/ 60`
 * arithmetic on `MAX_OFFSET_MINUTES` inside a component, further from the
 * predicate that bounds against the very same constant.
 */
export const OFFSET_TOO_FAR_HINT = `Hours and minutes, no more than ${MAX_OFFSET_MINUTES / 60} hr either side`;

/**
 * Minutes back into two fields, blanking a zero part.
 *
 * `60` is "1 hr", not "1 hr 0 min": a zero in a field a planner is not using is
 * noise they have to look past. A total of zero blanks *both* fields, which is
 * why a duration does not use this directly — see `durationFields`.
 */
export function fieldsFromMinutes(total: number): HoursMinutes {
  const absolute = Math.abs(total);
  const hours = Math.floor(absolute / 60);
  const minutes = absolute % 60;
  return {
    hours: hours === 0 ? '' : String(hours),
    minutes: minutes === 0 ? '' : String(minutes),
  };
}

/**
 * A stored duration as two fields, keeping a real zero visible.
 *
 * A blank pair means open-ended, so a stored `0` has to come back as a typed
 * zero rather than as blanks — otherwise opening the form on a deliberate
 * zero-length item and saving it again would quietly turn it into "runs to the
 * next one", which is the one conversion no planner asked for.
 */
export function durationFields(minutes: number | null): HoursMinutes {
  if (minutes === null) return EMPTY_HOURS_MINUTES;
  if (minutes === 0) return { hours: '', minutes: '0' };
  return fieldsFromMinutes(minutes);
}

/**
 * The two fields, their units, and whatever the caller puts after them.
 *
 * Wrapped in a fieldset so a screen reader reads the group before the field —
 * "How long it runs, Length in hours" — and labelled per instance, because a
 * form that asks for two spans of time has two pairs of these and "Hours" would
 * name both.
 */
export default function HoursMinutesField({
  idPrefix,
  legend,
  hoursLabel,
  minutesLabel,
  value,
  onChange,
  invalid = false,
  hint,
  trailing,
}: {
  idPrefix: string;
  legend: string;
  hoursLabel: string;
  minutesLabel: string;
  value: HoursMinutes;
  onChange: (value: HoursMinutes) => void;
  invalid?: boolean;
  /** What the pair means, read back as it is typed. */
  hint: string;
  /** A control that belongs to the same span — the before/after direction. */
  trailing?: ReactNode;
}) {
  return (
    <fieldset>
      {/* `px-0` because a legend carries 2px of browser inline padding, which
          leaves the group's label sitting two pixels right of the field under it
          and of every other label on the form. */}
      <legend className="label-caps mb-1 block px-0 text-[0.625rem] text-foreground/70">
        {legend}
      </legend>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          id={`${idPrefix}-hours`}
          aria-label={hoursLabel}
          className="w-14 tabular-nums"
          inputMode="numeric"
          autoComplete="off"
          placeholder="0"
          value={value.hours}
          aria-invalid={invalid || undefined}
          onChange={(e) => onChange({ ...value, hours: e.target.value })}
        />
        <span className="text-sm text-foreground/70">hr</span>
        <Input
          id={`${idPrefix}-minutes`}
          aria-label={minutesLabel}
          className="w-14 tabular-nums"
          inputMode="numeric"
          autoComplete="off"
          placeholder="0"
          value={value.minutes}
          aria-invalid={invalid || undefined}
          onChange={(e) => onChange({ ...value, minutes: e.target.value })}
        />
        <span className="text-sm text-foreground/70">min</span>
        {trailing}
      </div>
      <p className="mt-1 text-[0.6875rem] text-foreground/70">{hint}</p>
    </fieldset>
  );
}
